import { FC, Fragment } from 'react'
import {
  SectionArticlesTitle,
} from './styles'
import { JudgeRenewalQuestionsData } from 'src/dtos/renewalData'
import { handleYesNoAnswers } from 'src/utils/formDataManipulation'
import { IoMdCheckmark } from 'react-icons/io'
import { numberToCurrency } from 'src/utils/numberFormat'
import { TbFileCheck } from 'react-icons/tb'

interface JudgeRenewalQuestionsProps {
  questions?: JudgeRenewalQuestionsData;
  isComplete: boolean;
}

const JudgeRenewalQuestions: FC<JudgeRenewalQuestionsProps> = ({ questions, isComplete }) => {
  return (
    <section>
      <header>
        <h4>Renewal Questions</h4>
        {isComplete ? (
          <h6><IoMdCheckmark /> Complete</h6>
        ) : null}
      </header>
      <SectionArticlesTitle>QUESTIONS</SectionArticlesTitle>
      <article>
        <strong>
          In the last 12 months, has the applicant been the subject of a professional or judicial liability claim?
        </strong>
        <span>{handleYesNoAnswers(questions?.hasBeenSubjectOfClaim)}</span>
        {questions?.hasBeenSubjectOfClaim === true ? (
          <span><h6>Has the claim been previously reported to TLIE?:</h6> {handleYesNoAnswers(questions?.hasClaimBeenReported)}</span>
        ) : null}
      </article>
      <article>
        <strong>
          Does the applicant know, have reason to know, or have any basis to believe that a circumstance, act, error or
          omission might reasonably be expected to be the basis for a professional or judicial liability claim against the applicant?
        </strong>
        <span>{handleYesNoAnswers(questions?.hasBasisForClaim)}</span>
        {questions?.hasBasisForClaim === true ? (
          <span><h6>Has the claim been previously reported to TLIE?:</h6> {handleYesNoAnswers(questions?.hasBasisForClaimReported)}</span>
        ) : null}
      </article>
      <article>
        <strong>
          Has the applicant been the subject of a judicial disciplinary inquiry or action?
        </strong>
        <span>{handleYesNoAnswers(questions?.hasBeenSubjectOfAction)}</span>
        {questions?.hasBeenSubjectOfAction === true ? (
          <span>{questions?.hasBeenSubjectOfActionDetails ?? 'N/A'}</span>
        ) : null}
      </article>
      {questions?.claims && questions?.claims?.length > 0 ? questions.claims.map((claim, index) => (
        <Fragment key={index + 1}>
          <SectionArticlesTitle>CLAIM {index + 1}</SectionArticlesTitle>
          <article>
            <strong>Firm Name</strong>
            <span>{claim?.firmName ?? 'N/A'}</span>
          </article>
          <article>
            <strong>Claimant’s Full Name</strong>
            <span>{claim?.hasClaimant === false ? "I'm not sure" : (claim?.claimantFullName ?? 'N/A')}</span>
          </article>
          <article>
            <strong>Claim Type</strong>
            <span>{claim?.claimType ?? 'N/A'}</span>
          </article>
          <article>
            <strong>Date of Claim</strong>
            <span>{claim?.hasClaimDate === false ? "I'm not sure" : (claim?.claimDate ?? 'N/A')}</span>
          </article>
          <article>
            <strong>Date of Alleged Error</strong>
            <span>{claim?.hasAllegedErrorDate === false ? "I'm not sure" : (claim?.allegedErrorDate ?? 'N/A')}</span>
          </article>
          <article>
            <strong>Is this claim closed?</strong>
            <span>{claim?.isClaimClosed ?? 'N/A'}</span>
            {claim?.isClaimClosed === 'Yes' ? (
              <>
                <span>
                  <h6>Indicate how the claim was closed: </h6>
                  {claim?.closingType ?? 'N/A'}
                </span>
                <span>
                  <h6>Total loss paid including deductibles: </h6>
                  {claim?.totalLossPaid ? numberToCurrency(claim?.totalLossPaid) : 'N/A'}
                </span>
                <span>
                  <h6>Defense cost paid: </h6>
                  {claim?.defenseCostPaid ? numberToCurrency(claim?.defenseCostPaid) : 'N/A'}
                </span>
              </>
            ) : claim?.isClaimClosed === 'No' ? (
              <>
                <span>
                  <h6>Claimant’s settlement demand: </h6>
                  {claim?.claimantsSettlementDemand ? numberToCurrency(claim?.claimantsSettlementDemand) : 'N/A'}
                </span>
                <span>
                  <h6>Defendant’s offer for settlement: </h6>
                  {claim?.defendantsSettlementOffer ? numberToCurrency(claim?.defendantsSettlementOffer) : 'N/A'}
                </span>
                <span>
                  <h6>Insurer’s loss reserve: </h6>
                  {claim?.insurersLossReserve ? numberToCurrency(claim?.insurersLossReserve) : 'N/A'}
                </span>
                <span>
                  <h6>Deductible: </h6>
                  {claim?.closingCaseDeductible ? numberToCurrency(claim?.closingCaseDeductible) : 'N/A'}
                </span>
                <span>
                  <h6>Is claim in suit?: </h6>
                  {claim?.isClaimInSuit ?? 'N/A'}
                </span>
                {claim?.isClaimInSuit === 'Yes' ? (
                  <span>
                    <h6>Amount asked in summons: </h6>
                    {claim?.amountAskedSummons ? numberToCurrency(claim?.amountAskedSummons) : 'N/A'}
                  </span>
                ) : null}
                <span>
                  <h6>Total loss pain including deductibles: </h6>
                  {claim?.totalLossPain ? numberToCurrency(claim?.totalLossPain) : 'N/A'}
                </span>
              </>
            ) : null}
          </article>
          <article>
            <strong>Name of Insurer</strong>
            <span>{claim?.hasInsurer === false ? "I'm not sure" : (claim?.insurerName ?? 'N/A')}</span>
          </article>
          <article>
            <strong>Description of Claim</strong>
            <span>
              <h6>Alleged act, error, or omission upon which claimant bases claim:</h6>
            </span>
            <span>{claim?.hasClaimDescription === false ? "I'm not sure" : (claim?.claimDescription ?? 'N/A')}</span>
            <span>
              <h6>Description of case and events, including area of practice out of which the claim arose:</h6>
            </span>
            <span>{claim?.hasCaseDescription === false ? "I'm not sure" : (claim?.caseDescription ?? 'N/A')}</span>
            <span>
              <h6>Description of the type and extent of injury or damage allegedly sustained:</h6>
            </span>
            <span>{claim?.hasDamageDescription === false ? "I'm not sure" : (claim?.damageDescription ?? 'N/A')}</span>
            <span>
              <h6>Upload any applicable claim information:</h6>
            </span>
            {claim?.claimFile?.map(file => (
              <span key={file?.id} className="icon"><TbFileCheck/>{file?.fileName ?? 'N/A'}</span>
            ))}
          </article>
        </Fragment>
      )) : null}
      <SectionArticlesTitle>REVIEW</SectionArticlesTitle>
      <article>
        <strong>Notes</strong>
        <span>{questions?.reviewData?.note ?? 'N/A'}</span>
      </article>
      <article>
        <strong>Files</strong>
        {!!questions?.reviewData?.files && questions?.reviewData?.files.length > 0 ? 
          questions?.reviewData?.files.map(file => (
            <span key={file?.id} className="icon"><TbFileCheck /> {file?.fileName ?? "N/A"}</span>
          )) : (
            <span>N/A</span>
          )}
      </article>
    </section>
  )
}

export default JudgeRenewalQuestions